<template>
  <div class="booking-confirmation-timeout">
    <div class="container-fluid">
      <div class="row no-gutters">
        <div class="col-12 col-lg-8 offset-lg-2">
          <div class="booking-confirmation-timeout__interaction">
            <h2 class="text-3xl font-bold mt-5 mb-1 text-center">
              {{ $t('pages.bookingConfirmationTimeout.bookingNotCompleted.title')}}
            </h2>
            <CiInfoBox :type="'warning'" v-if="confirmation.internal_cancelled_info.payment === 'false' && confirmation.internal_cancelled_info.refund === 'false'">
              <h3 class="booking__headline">
                {{
                  $t(
                    'pages.bookingConfirmationTimeout.refundSuccess.infoBox.title'
                  )
                }}
              </h3>
              <p class="mb-4"> {{ $t('pages.bookingConfirmationTimeout.bookingNotCompleted.rejectedReason')}} </p>
              <p>{{ $t('pages.bookingConfirmationTimeout.bookingNotCompleted.retryInfo')}}</p>
              <CiButton
                class="navigation__button button--primary"
                @click="startOver()"
              >
                <span>{{
                  $t(
                    'pages.bookingConfirmationTimeout.refundSuccess.infoBox.button'
                  )
                }}</span>
                <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
              </CiButton>
            </CiInfoBox>
            <CiInfoBox :type="'information'" v-if="confirmation.internal_cancelled_info.payment === 'true' && confirmation.internal_cancelled_info.refund === 'true'">
              <h3 class="booking__headline">
                {{
                  $t(
                    'pages.bookingConfirmationTimeout.refundSuccess.infoBox.title'
                  )
                }}
              </h3>
              <p class="mb-4">
                <span>{{ $t('pages.bookingConfirmationTimeout.bookingNotCompleted.rejectedReason')}} </span> 
                <span v-html="$t('pages.bookingConfirmationTimeout.bookingNotCompleted.refundInfo')"></span>
              </p>
              <p>{{ $t('pages.bookingConfirmationTimeout.bookingNotCompleted.retryInfo')}}</p>
              <CiButton
                class="navigation__button button--primary"
                @click="startOver()"
              >
                <span>{{
                  $t(
                    'pages.bookingConfirmationTimeout.refundSuccess.infoBox.button'
                  )
                }}</span>
                <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
              </CiButton>
            </CiInfoBox>
            <CiInfoBox :type="'danger'" v-if="confirmation.internal_cancelled_info.payment === 'true' && confirmation.internal_cancelled_info.refund === 'false'">
              <h3 class="booking__headline">
                {{
                  $t(
                    'pages.bookingConfirmationTimeout.refundNoSuccess.infoBox.title'
                  )
                }}
              </h3>
              <p
                v-html="
                  $t(
                    'pages.bookingConfirmationTimeout.refundNoSuccess.infoBox.copy',
                    {
                      booking_number: confirmation.booking_number,
                      support_email: supportEmail,
                      email_subject: getRefundNoSuccessEmailSubject(),
                    }
                  )
                "
              ></p>
            </CiInfoBox>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import bookingDataMixin from '@/mixins/bookingDataMixin'
import bookingPriceMixin from '@/mixins/bookingPriceMixin'
import {
  CiButton,
  CiInfoBox,
  CiProgressBar,
  CiProgressNavigation,
} from '@/components'

import iconArrowWhite from '@/assets/icons/arrow-white.svg'
import iconCheckmarkWhite from '@/assets/icons/checkmark-white.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'

export default {
  name: 'BookingConfirmation',
  mixins: [bookingDataMixin, bookingPriceMixin],
  components: {
    CiButton,
    CiInfoBox,
    CiProgressBar,
    CiProgressNavigation,
    iconArrowWhite,
    iconCheckmarkWhite,
    iconWarning,
  },
  created() {
    this.setLoader(false)
  },
  computed: {
    ...mapGetters('booking', {
      confirmation: 'confirmation',
      dates: 'dates',
      price: 'price',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      countries: 'countries',
    }),

    supportEmail() {
      return process.env.VUE_APP_SUPPORT_EMAIL
    },    
  },
  methods: {
    ...mapActions('application', {
      setLoader: 'setLoader',
      resetStepStatus: 'resetStepStatus',
    }),

    ...mapActions('booking', {
      resetBookingState: 'resetState',
    }),

    startOver() {
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: 1 },
      })

      /* reset booking state */
      this.resetBookingState()

      /* reset step status */
      this.resetStepStatus()
    },

    getRefundNoSuccessEmailSubject() {
      return encodeURIComponent(
        `Refund not successful for Booking ${this.$route.params.bookingId}`
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.booking-confirmation-timeout {
  min-height: 100vh;

  @include media-breakpoint-up(lg) {
    > .container-fluid {
      padding: 0;
    }

    > .container-fluid,
    > .container-fluid > .row {
      min-height: 100vh;
    }
  }
}

.booking-confirmation-timeout__interaction {
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  position: relative;

  @include media-breakpoint-up(md) {
    padding: 0 20px 0;
  }

  @include media-breakpoint-up(lg) {
    padding: 0 30px 0;
  }

  @include media-breakpoint-up(xl) {
    padding: 0 35px 0;
  }

  .booking__headline {
    @include font-format(1rem, 1rem);

    margin: 0 0 15px 0;
  }

  .booking__subline {
    margin: 0;
  }
}


.booking {
  margin: 0;
  padding: 20px;
  position: relative;

  @include media-breakpoint-up(md) {
    padding: 35px;
  }

  &.booking--summary {
    padding-top: 10px;
    margin-bottom: 40px;
    &.warning {
      background-color: rgba(248, 231, 28, 0.15);
      border: 1px solid #f8e71c;
      border-radius: 4px;
      box-shadow: 0 2px 23px 0 #ededed;
    }
  }
}

.summary__step {
  @include font('gotham_bookregular');
  @include font-format(0.875rem, 1.125rem);

  border-top: 1px solid #979797;
  padding: 25px 0;
  position: relative;

  .step__label {
    @include font('gotham_mediumregular');
    @include font-format(0.875rem, 1.125rem);

    margin: 0 0 20px 0;

    &.step__label--inter {
      @include font('gotham_bookregular');

      margin: 0;
    }

    &.step__label--top {
      margin-top: 20px;
    }
  }

  .step__value {
    &.step__value--inter {
      margin-top: 20px;
    }
    &.step__value--margin-bottom {
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .wrapper {
      padding-right: 40px;

      @include media-breakpoint-up(md) {
        padding-right: 50px;
      }

      &.margin-top {
        margin-top: 20px;
      }

      &.no-padding {
        padding: 0;
      }
    }

    .value {
      @include font('gotham_mediumregular');
      @include font-format(0.875rem, 1.125rem);

      small {
        @include font('gotham_bookregular');
      }
    }

    .strike-through {
      text-decoration: line-through;
      margin-right: 0.4rem;
    }
    .hide-content {
      display: none !important;
    }

    .prices {
      display: block;
    }

    .season__label {
      margin-bottom: 10px;
    }
  }

  .step__edit {
    position: absolute;
    top: 20px;
    right: 0;
    cursor: pointer;
  }

  &.summary__step--border-bottom {
    border-bottom: 1px solid #979797;
  }

  &.summary__step--no-border {
    border: 0;
  }
}

.summary__sum {
  background-color: rgba(155, 155, 155, 0.1);
  height: 50px;
  margin: 10px 0;

  .sum-label {
    display: flex;
    align-items: center;
    margin-left: 15px;
    height: 100%;
  }

  .sum-value {
    margin-right: 15px;
  }
}

.sum.sum--completed {
  background-color: rgba(248, 231, 28, 0.4);
  color: black;
  align-items: center;
}
.strike-through {
  text-decoration: line-through;
  margin-right: 0.4rem;
}
.summary__warning {
  @include font('gotham_bookregular');
  @include font-format(0.75rem, 1.25rem);

  background-color: #fefcdd;
  border: 1px solid #f8e71c;
  margin: 15px 0 0 0;

  .warning-title {
    position: relative;
    background-color: #f8e71c66;
    padding: 10px 25px 10px 60px;

    .icon {
      position: absolute;
      left: 20px;
      top: 10px;
      width: 20px;
      height: auto;
    }
  }

  .warning-content {
    padding: 10px 25px 10px 60px;
  }
}
</style>
